// frontend/src/components/admin/forms/AnnouncementForm.jsx
import { useEffect, useState } from 'react';
import STORAGE_PATHS from '../../../constants/storagePaths';
import { uploadFile } from '../../../services/uploadFile';
import DatePicker from '../ui/DatePicker';
import FileUpload from '../ui/FileUpload';
import Input from '../ui/Input';
import Select from '../ui/Select';

const AnnouncementForm = ({ 
  initialValues, 
  onSubmit, 
  onCancel,
  campusesOptions = [], 
  ministriesOptions = [],
  cellGroupsOptions = []
}) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    publishDate: new Date(),
    imageUrl: '',
    targetType: 'all',
    campus: "",
    ministry: "",
    cellgroup: "",
    ...initialValues
  });

  const [imagePreview, setImagePreview] = useState(initialValues?.imageUrl || '');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Update form when initialValues change
  useEffect(() => {
    if (initialValues) {
      setFormData(prev => ({
        ...prev,
        ...initialValues,
        publishDate: initialValues.publishDate ? new Date(initialValues.publishDate) : new Date()
      }));
      setImagePreview(initialValues.imageUrl || '');
    }
  }, [initialValues]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: value,
      // Reset related IDs when target type changes
      ...(name === 'targetType' && {
        campusId: null,
        ministryId: null,
        cellgroupId: null
      })
    }));
  };

  const handleFileChange = async (file) => {
    try {
      // eslint-disable-next-line no-undef
      const imageUrl = await uploadFile(file, STORAGE_PATHS.ANNOUNCEMENTS);
      setFormData(prev => ({ ...prev, imageUrl }));
      setImagePreview(URL.createObjectURL(file));
    } catch (error) {
      console.error('Failed to upload image:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await onSubmit({
        ...formData,
        publishDate: formData.publishDate.toISOString()
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Input
        label="Title"
        name="title"
        value={formData.title}
        onChange={handleChange}
        required
      />
      
      <Input
        label="Description"
        name="description"
        value={formData.description}
        onChange={handleChange}
        type="textarea"
        required
      />
      
      <DatePicker
        label="Publish Date"
        selected={formData.publishDate}
        onChange={(date) => setFormData(prev => ({ ...prev, publishDate: date }))}
        required
      />
      
      <Select
        label="Target Audience"
        name="targetType"
        value={formData.targetType}
        onChange={handleChange}
        options={[
          { value: 'all', label: 'All Members' },
          { value: 'campus', label: 'Specific Campus' },
          { value: 'ministry', label: 'Specific Ministry' },
          { value: 'cellgroup', label: 'Specific Cell Group' },
        ]}
      />

      {formData.targetType === 'campus' && (
  <Select
    label="Select Campus"
    name="campus"  // Changed from campusId to campus
    value={formData.campus || ''}
    onChange={handleChange}
    options={[
      { value: '', label: 'Select a campus', disabled: true },
      ...campusesOptions  // Added spread operator
    ]}
    required={formData.targetType === 'campus'}
  />
)}

{formData.targetType === 'ministry' && (
  <Select
    label="Select Ministry"
    name="ministry"  // Changed from ministryId to ministry
    value={formData.ministry || ''}
    onChange={handleChange}
    options={[
      { value: '', label: 'Select a ministry', disabled: true },
      ...ministriesOptions  // Added spread operator
    ]}
    required={formData.targetType === 'ministry'}
  />
)}

{formData.targetType === 'cellgroup' && (
  <Select
    label="Select Cell Group"
    name="cellgroup"  // Changed from cellgroupId to cellgroup
    value={formData.cellgroup || ''}
    onChange={handleChange}
    options={[
      { value: '', label: 'Select a cell group', disabled: true },
      ...cellGroupsOptions  // Added spread operator
    ]}
    required={formData.targetType === 'cellgroup'}
  />
)}
      
      <FileUpload
        label="Featured Image"
        onFileChange={handleFileChange}
        preview={imagePreview}
        accept="image/*"
      />
      
      <div className="flex justify-end space-x-3 pt-4">
        <button
          type="button"
          className="btn btn-outline"
          onClick={onCancel}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving...' : 'Save Announcement'}
        </button>
      </div>
    </form>
  );
};

export default AnnouncementForm;